/**
 * PaymentCheckoutForm.jsx - Checkout form using useCheckout hook
 *
 * Works with CheckoutProvider and ui_mode: 'custom'
 * Includes ExpressCheckoutElement for Apple Pay / Google Pay
 */
import { useState } from 'react';
import {
  PaymentElement,
  ExpressCheckoutElement,
  useCheckout,
} from '@stripe/react-stripe-js/checkout';
import { usePaymentData } from './PaymentApp';
import logger from './logger';

const PaymentCheckoutForm = () => {
  const checkoutState = useCheckout();
  const { formattedTotal, formattedConvenienceFee, isUpdatingSession, cachedCustomerEmail } =
    usePaymentData();

  const [email, setEmail] = useState(cachedCustomerEmail || '');
  const [emailError, setEmailError] = useState(null);
  const [message, setMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expressPaymentAvailable, setExpressPaymentAvailable] = useState(false);

  // If email was provided at session creation, it's locked by Stripe
  const isEmailLocked = Boolean(cachedCustomerEmail);

  const payButtonLabel = formattedTotal ? `Pay ${formattedTotal} now` : 'Pay now';

  // Handle loading state
  if (checkoutState.type === 'loading') {
    return (
      <div className="payment-form-loading">
        <div className="payment-loading-spinner"></div>
        <p>Loading payment options...</p>
      </div>
    );
  }

  // Handle error state
  if (checkoutState.type === 'error') {
    return (
      <div className="payment-form-error">
        <p>Error: {checkoutState.error.message}</p>
      </div>
    );
  }

  const { checkout } = checkoutState;

  // Handle ExpressCheckoutElement ready event
  const handleExpressCheckoutReady = ({ availablePaymentMethods }) => {
    // Log detailed info for debugging Apple Pay / Google Pay availability
    const debugInfo = {
      methods: availablePaymentMethods,
      protocol: window.location.protocol,
      hostname: window.location.hostname,
    };
    console.log('=== Express Checkout Debug ===', debugInfo);

    // Show alert on mobile for easy debugging (remove in production)
    if (/iPhone|iPad|Android/i.test(navigator.userAgent)) {
      alert(
        `Express Pay Debug:\nApple Pay: ${availablePaymentMethods?.applePay || false}\nGoogle Pay: ${availablePaymentMethods?.googlePay || false}\nLink: ${availablePaymentMethods?.link || false}\nHTTPS: ${window.location.protocol === 'https:'}`
      );
    }

    if (availablePaymentMethods) {
      const hasExpress =
        availablePaymentMethods.applePay ||
        availablePaymentMethods.googlePay ||
        availablePaymentMethods.link;
      setExpressPaymentAvailable(hasExpress);
      logger.info('Express payment methods available:', availablePaymentMethods);
    }
  };

  // Handle ExpressCheckoutElement confirm event
  const handleExpressCheckoutConfirm = async (event) => {
    logger.info('Express checkout confirm event:', event);
    setIsSubmitting(true);
    setMessage(null);

    try {
      const result = await checkout.confirm({
        expressCheckoutConfirmEvent: event,
      });

      if (result.type === 'error') {
        logger.error('Express payment failed:', result.error);
        setMessage(result.error.message || 'Payment failed');
        setIsSubmitting(false);
      }
      // If successful, Stripe redirects to return_url
    } catch (err) {
      logger.error('Express payment error:', err);
      setMessage(`Error: ${err.message}`);
      setIsSubmitting(false);
    }
  };

  // Handle ExpressCheckoutElement cancel event
  const handleExpressCheckoutCancel = () => {
    logger.info('Express checkout cancelled by user');
    setIsSubmitting(false);
  };

  const validateEmail = (emailValue) => {
    if (!emailValue) {
      return { isValid: false, message: 'Email is required' };
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailValue)) {
      return { isValid: false, message: 'Please enter a valid email' };
    }
    return { isValid: true, message: null };
  };

  const handleEmailBlur = async () => {
    if (!email || isEmailLocked) return;
    const { isValid, message: msg } = validateEmail(email);
    if (!isValid) {
      setEmailError(msg);
    } else {
      // Update email in checkout session (only if not pre-filled)
      try {
        await checkout.updateEmail(email);
      } catch (err) {
        logger.error('Failed to update email:', err);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email
    const { isValid, message: validationMsg } = validateEmail(email);
    if (!isValid) {
      setEmailError(validationMsg);
      setMessage(validationMsg);
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    try {
      // Update email before confirming (only if not pre-filled at session creation)
      if (!isEmailLocked) {
        await checkout.updateEmail(email);
      }

      // Confirm the payment
      logger.info('Confirming payment...');
      const result = await checkout.confirm();

      if (result.type === 'error') {
        logger.error('Payment failed:', result.error);
        setMessage(result.error.message || 'Payment failed');
        setIsSubmitting(false);
      }
      // If successful, Stripe redirects to return_url
    } catch (err) {
      logger.error('Payment error:', err);
      setMessage(`Error: ${err.message}`);
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      {/* Top divider line */}
      <div className="payment-divider-line"></div>

      {/* Express Checkout - Apple Pay / Google Pay / Link */}
      <div className="express-checkout-container">
        <ExpressCheckoutElement
          onReady={handleExpressCheckoutReady}
          onConfirm={handleExpressCheckoutConfirm}
          onCancel={handleExpressCheckoutCancel}
          options={{
            buttonHeight: 48,
            buttonTheme: {
              applePay: 'black',
              googlePay: 'black',
            },
            buttonType: {
              applePay: 'plain',
              googlePay: 'plain',
            },
            layout: {
              maxColumns: 2,
              maxRows: 1,
            },
          }}
        />
      </div>

      {/* Divider between express and regular payment */}
      {expressPaymentAvailable && (
        <div className="payment-divider">
          <span>Or pay with card</span>
        </div>
      )}

      <label>
        Email
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => {
            if (isEmailLocked) return;
            setEmailError(null);
            setEmail(e.target.value);
          }}
          onBlur={handleEmailBlur}
          className={`${emailError ? 'error' : ''} ${isEmailLocked ? 'locked' : ''}`}
          placeholder="you@example.com"
          disabled={isEmailLocked}
          readOnly={isEmailLocked}
        />
      </label>
      {emailError && <div id="email-errors">{emailError}</div>}

      <h4>Payment</h4>
      <PaymentElement id="payment-element" options={{ layout: 'accordion' }} />

      <button disabled={isSubmitting || isUpdatingSession} id="submit">
        {isSubmitting ? (
          <div className="spinner"></div>
        ) : isUpdatingSession ? (
          'Updating...'
        ) : (
          payButtonLabel
        )}
      </button>

      <p className="convenience-fee-note">Convenience fee of {formattedConvenienceFee} was added</p>

      {message && <div id="payment-message">{message}</div>}
    </form>
  );
};

export default PaymentCheckoutForm;
